import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@shared/lib/utils";
import { Button } from "@shared/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@shared/ui/dialog";
import { X } from "lucide-react";
import * as React from "react";

export interface ImageProps extends React.ComponentProps<"img"> {
	src: string;
	alt: string;
	width?: number | string;
	height?: number | string;
	aspectRatio?: "square" | "video" | "auto" | string;
	blurDataURL?: string;
	allowFullscreen?: boolean; // Новый проп для включения fullscreen режима
}

export function Image({
	className,
	src,
	alt,
	width,
	height,
	aspectRatio = "auto",
	blurDataURL,
	loading = "lazy",
	allowFullscreen = false,
	ref,
	...props
}: ImageProps) {
	const [isLoaded, setIsLoaded] = React.useState(false);
	const [isError, setIsError] = React.useState(false);

	const aspectStyles =
		{
			square: "aspect-square",
			video: "aspect-video",
			auto: "",
		}[aspectRatio] || aspectRatio;

	// Базовый контент изображения с эффектами загрузки
	const ImageCore = (
		<>
			{blurDataURL && !isLoaded && !isError && (
				<div
					className="absolute inset-0 bg-cover bg-center filter blur-xl scale-110 transition-opacity duration-500"
					style={{ backgroundImage: `url(${blurDataURL})` }}
				/>
			)}

			{!blurDataURL && !isLoaded && !isError && (
				<div className="absolute inset-0 animate-pulse bg-neutral-200 dark:bg-neutral-800" />
			)}

			{isError && !isLoaded && (
				<div className="absolute inset-0 flex items-center justify-center bg-neutral-100 text-xs text-muted-foreground dark:bg-neutral-900">
					❌ Ошибка загрузки
				</div>
			)}

			<img
				ref={ref}
				src={src}
				alt={alt}
				loading={loading}
				decoding="async"
				onLoad={() => setIsLoaded(true)}
				onError={() => setIsError(true)}
				className={cn(
					"h-full w-full object-cover transition-all duration-500 ease-in-out",
					isLoaded ? "scale-100 opacity-100 blur-0" : "scale-105 opacity-0 blur-md",
					allowFullscreen && "cursor-zoom-in" // Меняем курсор, если доступен fullscreen
				)}
				{...props}
			/>
		</>
	);

	// Обертка-контейнер, задающая размеры
	const containerOffsetStyles = cn(
		"relative overflow-hidden bg-muted group",
		aspectStyles,
		className
	);
	const containerInlineStyles = {
		width: width ? (typeof width === "number" ? `${width}px` : width) : "100%",
		height: height ? (typeof height === "number" ? `${height}px` : height) : "auto",
	};

	// ЕСЛИ включен fullscreen режим
	if (allowFullscreen && !isError) {
		return (
			<Dialog>
				<DialogTrigger asChild>
					<button
						className={containerOffsetStyles}
						style={containerInlineStyles}
						type="button"
						aria-label={`Увеличить изображение: ${alt}`}
					>
						{ImageCore}
					</button>
				</DialogTrigger>
				<DialogTitle></DialogTitle>
				<DialogContent
					showCloseButton={false}
					className="max-w-full! z-50! max-h-full! p-0 w-full h-full  bg-transparent border-none shadow-none flex items-center justify-center sm:rounded-none backdrop-blur-sm"
				>
					<img
						src={src}
						alt={alt}
						className="max-w-full max-h-[90vh] object-contain rounded-lg select-none animate-in zoom-in-95 duration-200"
					/>
					<DialogPrimitive.Close className="absolute right-4 top-4 md:right-5 md:top-5 rounded-full transition-all duration-200  ">
						<Button variant="destructive" className="cursor-pointer">
							<X className="h-5 w-5" />
							<span className="sr-only">Закрыть</span>
						</Button>
					</DialogPrimitive.Close>
				</DialogContent>
			</Dialog>
		);
	}

	// ОБЫЧНЫЙ режим (без fullscreen)
	return (
		<div className={containerOffsetStyles} style={containerInlineStyles}>
			{ImageCore}
		</div>
	);
}
